/**
 * Create Recipe Modal
 *
 * Simple modal to create a new recipe with just a name.
 * After creation, opens the RecipeBuilder drawer to add ingredients.
 */

import { useState, useEffect } from 'react';
import { ChefHat } from 'lucide-react';
import { Modal, ModalFooter } from '@/components/ui';
import { Button } from '@/components/ui';
import { useCreateRecipe } from '../hooks';

interface CreateRecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (recipeId: string) => void;
  /** Pre-fill with a POS product ID for automatic association */
  defaultPosProductId?: string;
  /** Pre-fill the recipe name (e.g., from POS product name) */
  defaultName?: string;
}

export function CreateRecipeModal({
  isOpen,
  onClose,
  onSuccess,
  defaultPosProductId,
  defaultName = '',
}: CreateRecipeModalProps) {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const createRecipe = useCreateRecipe();

  // Reset name when modal opens with new defaults
  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setDescription('');
    }
  }, [isOpen, defaultName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      const recipe = await createRecipe.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        posProductIds: defaultPosProductId ? [defaultPosProductId] : [],
        ingredients: [], // Empty ingredients allowed, user adds them in builder
      });

      setName('');
      setDescription('');
      onClose();
      onSuccess?.(recipe.id);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nouvelle recette"
      description="Creez une fiche technique"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Icon header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
            <ChefHat className="w-5 h-5 text-primary-500" />
          </div>
        </div>

        <div>
          <label htmlFor="recipe-name" className="block text-sm font-medium text-dark-500 mb-1">
            Nom de la recette *
          </label>
          <input
            id="recipe-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Big Mac"
            className="w-full px-4 py-2 border border-neutral-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="recipe-description" className="block text-sm font-medium text-dark-500 mb-1">
            Description
          </label>
          <textarea
            id="recipe-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description optionnelle..."
            rows={3}
            className="w-full px-4 py-2 border border-neutral-200 rounded-lg text-sm resize-none
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <p className="text-sm text-neutral-500">
          Apres la creation, vous pourrez ajouter les ingredients dans le builder.
        </p>

        {/* Actions */}
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || createRecipe.isPending}
          >
            {createRecipe.isPending ? 'Creation...' : 'Creer la recette'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
